import { createContext, useContext, useEffect, useState, type ReactElement, type ReactNode } from 'react';

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const breakpoints: Breakpoint[] = ['sm', 'md', 'lg', 'xl', '2xl'];

const breakpointWidthVariables: Record<Breakpoint, string> = {
    sm: '--breakpoint-sm',
    md: '--breakpoint-md',
    lg: '--breakpoint-lg',
    xl: '--breakpoint-xl',
    '2xl': '--breakpoint-2xl',
};

function breakpointQueries(): MediaQueryList[] {
    const styles = getComputedStyle(document.documentElement);
    return breakpoints.map((name) =>
        window.matchMedia(`(min-width: ${styles.getPropertyValue(breakpointWidthVariables[name]).trim()})`),
    );
}

function getWidestMatch(queries: MediaQueryList[]): Breakpoint | undefined {
    let widest: Breakpoint | undefined;
    for (const [index, name] of breakpoints.entries()) {
        if (queries[index]?.matches) {
            widest = name;
        }
    }
    return widest;
}

const BreakpointContext = createContext<Breakpoint | undefined>(undefined);

export type BreakpointProviderProps = {
    children?: ReactNode;
};

export function BreakpointProvider({ children }: BreakpointProviderProps): ReactElement {
    const [widest, setWidest] = useState<Breakpoint | undefined>(() => getWidestMatch(breakpointQueries()));

    useEffect(() => {
        const queries = breakpointQueries();
        const sync = (): void => {
            setWidest(getWidestMatch(queries));
        };
        sync();
        for (const query of queries) {
            query.addEventListener('change', sync);
        }
        return () => {
            for (const query of queries) {
                query.removeEventListener('change', sync);
            }
        };
    }, []);

    return <BreakpointContext.Provider value={widest}>{children}</BreakpointContext.Provider>;
}

export function useBreakpoint(name: Breakpoint): boolean {
    const widest = useContext(BreakpointContext);
    return widest !== undefined && breakpoints.indexOf(widest) >= breakpoints.indexOf(name);
}
